'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Button, Card, CardBody, Tabs, Tab, Select, SelectItem, Tooltip, Switch } from "@nextui-org/react";
import { GitCompare, Edit, Copy, Download, Upload, Trash2, Code, FileText, RefreshCw } from "lucide-react";
import ReactDiffViewer from 'react-diff-viewer-continued';
import { copyToClipboard, saveText } from "@/lib/utils";
import { jsonExample, xmlExample, yamlExample } from "@/lib/samples";
import { useTheme } from "@/app/ThemeProvider";
import ResizableSplit from "@/components/ResizableSplit";
import { canonicalizeForDiff, StructuralFormat } from "@/lib/canonicalize";
import { trackToolAction } from "@/lib/analytics";

// Sample text for different formats
const textExamples = {
    text: `This is a sample text.
It has multiple lines.
You can use this as a starting point.
Try making some changes to see the diff.`,
    code: `function helloWorld() {
  console.log("Hello, world!");
  return true;
}

// Call the function
helloWorld();`,
    json: jsonExample,
    xml: xmlExample,
    yaml: yamlExample
};

// Modified versions of the examples with some differences
const modifiedExamples = {
    text: `This is a sample text.
It has multiple lines and some changes.
You can use this as a starting point.
Try making more changes to see the diff in action.`,
    code: `function helloWorld() {
  // Added a comment
  console.log("Hello, beautiful world!");
  return true;
}

// Call the function
const result = helloWorld();
console.log(result);`,
    json: jsonExample.replace('"type": "donut"', '"type": "pastry"').replace('0.55', '0.75'),
    xml: xmlExample.replace('<genre>Computer</genre>', '<genre>Programming</genre>').replace('44.95', '49.99'),
    yaml: yamlExample.replace('pi: 3.14159', 'pi: 3.14').replace('- fred', '- daffy')
};

// Example text is now generic, no need for format options

/** Props for DiffChecker. */
interface DiffCheckerProps {
    /**
     * Compare mode selected on first render: "text" for a plain line diff, or a format name to compare parsed
     * documents (keys sorted, whitespace ignored). Landing pages such as /json-compare set it; /diffchecker leaves it unset.
     */
    initialMode?: 'text' | StructuralFormat;
}

const DiffChecker = ({ initialMode = 'text' }: DiffCheckerProps) => {
    const [originalText, setOriginalText] = useState('');
    const [modifiedText, setModifiedText] = useState('');
    const [showDiff, setShowDiff] = useState(false);
    const [splitView, setSplitView] = useState(true);
    const [showWordDiff, setShowWordDiff] = useState(true);
    const [showLineNumbers, setShowLineNumbers] = useState(true);
    const [showOnlyDiffs, setShowOnlyDiffs] = useState(false);
    const [useDarkTheme, setUseDarkTheme] = useState(false);
    const [extraLines, setExtraLines] = useState(3);
    const { theme } = useTheme();
    const [compareMode, setCompareMode] = useState<'text' | StructuralFormat>(initialMode);
    const [canonicalLeft, setCanonicalLeft] = useState('');
    const [canonicalRight, setCanonicalRight] = useState('');
    const [canonicalErrors, setCanonicalErrors] = useState<{ left?: string; right?: string }>({});

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Initialize with example text when component loads
    useEffect(() => {
        // Only load example on initial render
        setOriginalText(textExamples[initialMode]);
        setModifiedText(modifiedExamples[initialMode]);
        // Only the first render's mode matters; later mode changes load their own examples in handleCompareModeChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the diff viewer's theme in sync with the site theme
    useEffect(() => {
        setUseDarkTheme(theme === 'dark');
    }, [theme]);

    // In a structural mode, canonicalize both sides (recursively sorted keys,
    // consistent formatting) before diffing so only real content changes show up
    useEffect(() => {
        if (compareMode === 'text') return;
        let cancelled = false;
        (async () => {
            const [left, right] = await Promise.all([
                canonicalizeForDiff(originalText, compareMode),
                canonicalizeForDiff(modifiedText, compareMode),
            ]);
            if (cancelled) return;
            setCanonicalErrors({ left: left.error, right: right.error });
            setCanonicalLeft(left.text ?? '');
            setCanonicalRight(right.text ?? '');
        })();
        return () => {
            cancelled = true;
        };
    }, [compareMode, originalText, modifiedText]);

    // Load example text - format-specific when a structural compare mode is active
    const loadExample = () => {
        const key = compareMode === 'text' ? 'text' : compareMode;
        setOriginalText(textExamples[key]);
        setModifiedText(modifiedExamples[key]);
    };

    // Switching format should reset to that format's own example - otherwise
    // leftover Text-mode content just fails to parse as JSON/YAML/XML
    const handleCompareModeChange = (mode: 'text' | StructuralFormat) => {
        trackToolAction("diff", "mode_change", mode);
        setCompareMode(mode);
        setOriginalText(textExamples[mode]);
        setModifiedText(modifiedExamples[mode]);
    };

    // Clear both text areas
    const clearText = () => {
        setOriginalText('');
        setModifiedText('');
    };

    // Handle file upload for original text
    const handleOriginalFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    setOriginalText(event.target.result as string);
                }
            };
            reader.readAsText(file);
        }
    };

    // Handle file upload for modified text
    const handleModifiedFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    setModifiedText(event.target.result as string);
                }
            };
            reader.readAsText(file);
        }
    };

    // Download diff result
    const downloadDiff = () => {
        trackToolAction("diff", "download");
        // Create a simple text representation of the diff
        const diffText = `Original Text:\n${originalText}\n\nModified Text:\n${modifiedText}`;
        saveText(diffText, 'diff');
    };

    // Simple formatter for code display
    const formatCode = (str: string) => {
        if (!str) return <span></span>;
        return <pre className="font-mono text-sm">{str}</pre>;
    };

    const renderDiffView = () => {
        const isStructural = compareMode !== 'text';
        // In structural mode, diff the canonicalized (sorted-key) text instead of the raw input
        const oldValue = isStructural ? canonicalLeft : originalText;
        const newValue = isStructural ? canonicalRight : modifiedText;
        const leftError = isStructural ? canonicalErrors.left : undefined;
        const rightError = isStructural ? canonicalErrors.right : undefined;
        const valuesAreIdentical = oldValue === newValue;

        return (
            <div className="w-full">
                <div className="flex flex-wrap gap-2 mb-4 justify-between">
                    <div className="flex flex-wrap gap-2">
                        <Tooltip content="Toggle between split and inline view">
                            <Switch
                                isSelected={splitView}
                                onValueChange={setSplitView}
                                size="sm"
                                color="primary"
                            >
                                Split View
                            </Switch>
                        </Tooltip>
                        <Tooltip content="Show or hide word-level differences">
                            <Switch
                                isSelected={showWordDiff}
                                onValueChange={setShowWordDiff}
                                size="sm"
                                color="primary"
                            >
                                Word Diff
                            </Switch>
                        </Tooltip>
                        <Tooltip content="Show or hide line numbers">
                            <Switch
                                isSelected={showLineNumbers}
                                onValueChange={setShowLineNumbers}
                                size="sm"
                                color="primary"
                            >
                                Line Numbers
                            </Switch>
                        </Tooltip>
                        <Tooltip content="Show only lines with differences">
                            <Switch
                                isSelected={showOnlyDiffs}
                                onValueChange={setShowOnlyDiffs}
                                size="sm"
                                color="primary"
                            >
                                Show Diffs Only
                            </Switch>
                        </Tooltip>
                        <Tooltip content="Toggle between light and dark theme">
                            <Switch
                                isSelected={useDarkTheme}
                                onValueChange={setUseDarkTheme}
                                size="sm"
                                color="primary"
                            >
                                Dark Theme
                            </Switch>
                        </Tooltip>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            color="primary"
                            variant="bordered"
                            radius="sm"
                            startContent={<Download className="w-4 h-4" />}
                            onClick={downloadDiff}
                        >
                            Download
                        </Button>
                    </div>
                </div>

                {leftError || rightError ? (
                    <div className="flex flex-col gap-3">
                        {leftError && (
                            <div className="p-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg">
                                <p className="font-medium mb-1">Original text is not valid {compareMode.toUpperCase()}</p>
                                <p className="text-sm">{leftError}</p>
                            </div>
                        )}
                        {rightError && (
                            <div className="p-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg">
                                <p className="font-medium mb-1">Modified text is not valid {compareMode.toUpperCase()}</p>
                                <p className="text-sm">{rightError}</p>
                            </div>
                        )}
                    </div>
                ) : valuesAreIdentical ? (
                    <Card className="w-full mb-4">
                        <CardBody className="flex items-center justify-center p-8 bg-green-50 dark:bg-green-950">
                            <div className="text-center">
                                <div className="text-green-600 dark:text-green-400 text-xl font-semibold mb-2">
                                    {isStructural ? 'No structural differences' : 'The texts are identical'}
                                </div>
                                <p className="text-green-700 dark:text-green-300">
                                    {isStructural
                                        ? 'Both documents are equivalent — formatting, whitespace, and key order are ignored.'
                                        : 'There are no differences between the original and modified text.'}
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                ) : (
                    <ReactDiffViewer
                        oldValue={oldValue}
                        newValue={newValue}
                        splitView={splitView}
                        disableWordDiff={!showWordDiff}
                        showDiffOnly={showOnlyDiffs}
                        hideLineNumbers={!showLineNumbers}
                        extraLinesSurroundingDiff={extraLines}
                        useDarkTheme={useDarkTheme}
                        leftTitle="Original Text"
                        rightTitle="Modified Text"
                        renderContent={formatCode}
                        styles={{
                            variables: {
                                light: {
                                    diffViewerBackground: '#fff',
                                    diffViewerTitleBackground: '#fafafa',
                                    addedBackground: '#e6ffed',
                                    addedColor: '#24292e',
                                    removedBackground: '#ffebe9',
                                    removedColor: '#24292e',
                                    wordAddedBackground: '#acf2bd',
                                    wordRemovedBackground: '#fdb8c0',
                                },
                            },
                        }}
                    />
                )}
            </div>
        );
    };

    const renderEditView = () => {
        return (
            <div className="w-full flex flex-col">
                <div className="flex flex-wrap gap-2 mb-4 justify-between">
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            color="primary"
                            variant="bordered"
                            radius="sm"
                            startContent={<RefreshCw className="w-4 h-4" />}
                            onClick={loadExample}
                        >
                            Load Example
                        </Button>
                        <Button
                            size="sm"
                            color="danger"
                            variant="bordered"
                            radius="sm"
                            startContent={<Trash2 className="w-4 h-4" />}
                            onClick={clearText}
                        >
                            Clear All
                        </Button>
                    </div>
                </div>

                <ResizableSplit
                    storageKey="split-diffchecker-edit"
                    leftLabel="original text"
                    rightLabel="modified text"
                    left={
                        <Card className="w-full h-full">
                            <CardBody className="p-0">
                                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-800">
                                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">Original Text</label>
                                    <div className="flex gap-2">
                                        <Tooltip content="Upload a file">
                                            <Button
                                                size="sm"
                                                variant="bordered"
                                                radius="sm"
                                                isIconOnly
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <Upload className="w-4 h-4" />
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleOriginalFileUpload}
                                                    className="hidden"
                                                />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip content="Copy text">
                                            <Button
                                                size="sm"
                                                variant="bordered"
                                                radius="sm"
                                                isIconOnly
                                                onClick={() => copyToClipboard(originalText)}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                                <textarea
                                    value={originalText}
                                    onChange={(e) => setOriginalText(e.target.value)}
                                    className="w-full h-[500px] p-4 font-mono text-sm border-t dark:border-gray-700 resize-none focus:outline-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                                    placeholder="Enter or paste your original text here..."
                                />
                            </CardBody>
                        </Card>
                    }
                    right={
                        <Card className="w-full h-full">
                            <CardBody className="p-0">
                                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-800">
                                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">Modified Text</label>
                                    <div className="flex gap-2">
                                        <Tooltip content="Upload a file">
                                            <Button
                                                size="sm"
                                                variant="bordered"
                                                radius="sm"
                                                isIconOnly
                                                onClick={() => {
                                                    const input = document.createElement('input');
                                                    input.type = 'file';
                                                    input.onchange = (e) => handleModifiedFileUpload(e as any);
                                                    input.click();
                                                }}
                                            >
                                                <Upload className="w-4 h-4" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip content="Copy text">
                                            <Button
                                                size="sm"
                                                variant="bordered"
                                                radius="sm"
                                                isIconOnly
                                                onClick={() => copyToClipboard(modifiedText)}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                                <textarea
                                    value={modifiedText}
                                    onChange={(e) => setModifiedText(e.target.value)}
                                    className="w-full h-[500px] p-4 font-mono text-sm border-t dark:border-gray-700 resize-none focus:outline-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                                    placeholder="Enter or paste your modified text here..."
                                />
                            </CardBody>
                        </Card>
                    }
                />
            </div>
        );
    };

    return (
        <div className="w-full flex flex-col items-center gap-6 p-4">
            <Card className="w-full">
                <CardBody>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        This tool helps you compare two texts and highlight the differences between them.
                        You can upload files, use example templates, or paste your own text.
                        Switch between edit mode and diff view to see the changes.
                    </p>

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Compare as:</span>
                        <div className="flex flex-wrap gap-2">
                            {(['text', 'json', 'yaml', 'xml'] as const).map((mode) => (
                                <Button
                                    key={mode}
                                    size="sm"
                                    radius="sm"
                                    variant={compareMode === mode ? 'solid' : 'flat'}
                                    color={compareMode === mode ? 'primary' : 'default'}
                                    onClick={() => handleCompareModeChange(mode)}
                                >
                                    {mode === 'text' ? 'Text' : mode.toUpperCase()}
                                </Button>
                            ))}
                        </div>
                        {compareMode !== 'text' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Comparing {compareMode.toUpperCase()} elements, not raw text — formatting, whitespace, and key order are ignored.
                            </span>
                        )}
                    </div>

                    {showDiff ? renderDiffView() : renderEditView()}

                    <div className="flex justify-center mt-6">
                        <Button
                            color="primary"
                            variant="bordered"
                            radius="sm"
                            startContent={showDiff ? <Edit className="w-4 h-4" /> : <GitCompare className="w-4 h-4" />}
                            onClick={() => {
                                if (!showDiff) trackToolAction("diff", "compare", compareMode);
                                setShowDiff(!showDiff);
                            }}
                            className="min-w-[160px]"
                        >
                            {showDiff ? 'Edit Text' : 'Show Diff'}
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default DiffChecker;
